import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import CmsImage from '../../components/CmsImage';
import Lightbox from '../../components/Lightbox';
import ContactForm from '../../components/ContactForm';
import { fetchLocalizedCollection, entryMatchesSlug } from '../../lib/cms';
import { parseSpecsList } from '../SingleProductPage';

export default function SingleProductPageEn() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    Promise.all([
      fetchLocalizedCollection('produkter', 'en'),
      fetchLocalizedCollection('ydelser_produkter', 'en'),
    ])
      .then(([products, categories]) => {
        if (!isMounted) return;
        const matched = (products || []).find((p) => entryMatchesSlug(p, slug, 'en') || entryMatchesSlug(p, slug, 'da'));
        setProduct(matched || null);

        if (matched) {
          const categorySlug =
            matched.terms?.produktkategori?.[0] || 'kantpresser';
          const cat = (categories || []).find((c) => entryMatchesSlug(c, categorySlug, 'en') || entryMatchesSlug(c, categorySlug, 'da'));
          setCategoryInfo(cat || { titel: 'Press Brakes', slug: 'press-brakes' });
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching product data:', err);
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-stone-50">
        <div className="flex items-center gap-3 text-stone-600">
          <i className="fa-solid fa-circle-notch fa-spin text-xl text-[#006368]" />
          <span>Loading product data...</span>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-stone-50 px-4 text-center">
        <h1 className="text-2xl font-bold text-stone-900">Product Not Found</h1>
        <p className="text-stone-600 mt-2 text-sm">The requested machine does not exist or has been retired.</p>
        <div className="mt-6 flex flex-wrap gap-4 justify-center">
          <Link
            to="/en/press-brakes"
            className="px-6 py-2.5 rounded bg-[#006368] text-white font-semibold text-sm hover:bg-[#004d51] transition"
          >
            Browse all press brakes
          </Link>
          <Link
            to="/en"
            className="px-6 py-2.5 rounded bg-stone-200 text-stone-800 font-semibold text-sm hover:bg-stone-300 transition"
          >
            Go to home
          </Link>
        </div>
      </div>
    );
  }

  const mainImage = Array.isArray(product.billede) ? product.billede[0] : product.billede;
  const manufacturer =
    product.producent_tekst ||
    (Array.isArray(product.terms?.producent) ? product.terms.producent[0]?.toUpperCase() : '') ||
    'MVD';

  const categoryTitle = categoryInfo?.titel || 'Press Brakes';
  const categoryLink = categoryInfo?.currentSlug ? `/en/${categoryInfo.currentSlug}` : '/en/press-brakes';

  const parsedSpecs = parseSpecsList(product.specifikationer);
  const keySpecs = parsedSpecs.slice(0, 4);

  return (
    <>
      <Helmet>
        <title>{`${product.titel} ${product.model ? `(${product.model})` : ''} | POVL MØLLERS MASKINFABRIK A/S`}</title>
        {product.kort_beskrivelse && (
          <meta name="description" content={product.kort_beskrivelse} />
        )}
        <meta
          property="og:title"
          content={`${product.titel} ${product.model ? `(${product.model})` : ''}`}
        />
        {product.kort_beskrivelse && (
          <meta property="og:description" content={product.kort_beskrivelse} />
        )}
        {mainImage && <meta property="og:image" content={mainImage} />}
      </Helmet>

      {/* Breadcrumb Bar */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2 flex-wrap">
          <Link to="/en" className="hover:text-[#006368] transition">
            Home
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <Link to={categoryLink} className="hover:text-[#006368] transition">
            {categoryTitle}
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">{product.titel}</span>
        </div>
      </div>

      {/* Main Top Product Showcase Section */}
      <section className="py-12 lg:py-16 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            {/* Left side: Large Product Image with natural proportions */}
            <div className="lg:col-span-6 xl:col-span-7">
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-6 sm:p-10 flex flex-col items-center justify-center relative group">
                {mainImage ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setLightboxOpen(true)}
                      className="w-full flex items-center justify-center cursor-zoom-in"
                      title="Click to zoom image"
                    >
                      <CmsImage
                        src={mainImage}
                        alt={`${product.titel} - ${product.model || ''}`}
                        width={1200}
                        className="max-h-[500px] w-auto h-auto max-w-full object-contain"
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => setLightboxOpen(true)}
                      className="absolute bottom-4 right-4 bg-white/90 hover:bg-white text-stone-700 hover:text-[#006368] px-3 py-1.5 rounded-md text-xs font-semibold shadow-sm border border-stone-200 flex items-center gap-1.5 transition"
                    >
                      <i className="fa-solid fa-magnifying-glass-plus" />
                      <span>Zoom</span>
                    </button>
                  </>
                ) : (
                  <div className="h-64 flex flex-col items-center justify-center text-stone-400">
                    <i className="fa-solid fa-industry text-5xl mb-3 text-stone-300" />
                    <span className="text-sm">Image coming soon</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right side: Product Information & Core Specifications */}
            <div className="lg:col-span-6 xl:col-span-5 flex flex-col justify-start">
              {/* Category & Manufacturer Badge */}
              <div className="flex items-center gap-2 mb-3">
                {manufacturer && (
                  <span className="px-2.5 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider">
                    {manufacturer}
                  </span>
                )}
                <Link
                  to={categoryLink}
                  className="text-xs text-stone-500 hover:text-[#006368] font-medium transition"
                >
                  {categoryTitle}
                </Link>
              </div>

              {/* Product Title */}
              <h1 className="text-2xl sm:text-4xl font-extrabold text-stone-900 tracking-tight leading-tight">
                {product.titel}
              </h1>

              {/* Model & Item number */}
              <div className="mt-2 flex flex-wrap items-center gap-y-1 gap-x-4 text-sm text-stone-600">
                {product.model && (
                  <span className="font-semibold text-stone-800">{product.model}</span>
                )}
                {product.varenummer && (
                  <span className="text-stone-500 font-mono text-xs bg-stone-100 px-2 py-0.5 rounded border border-stone-200">
                    Item no: {product.varenummer}
                  </span>
                )}
              </div>

              {/* Short Intro */}
              {product.kort_beskrivelse && (
                <p className="mt-5 text-stone-700 text-base leading-relaxed">
                  {product.kort_beskrivelse}
                </p>
              )}

              {/* Core Specifications Highlight Box */}
              {keySpecs.length > 0 && (
                <div className="mt-6 p-4 rounded-lg bg-stone-50 border border-stone-200">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-3 flex items-center gap-1.5">
                    <i className="fa-solid fa-sliders text-[#006368]" />
                    <span>Key Specifications</span>
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {keySpecs.map((spec, i) => (
                      <div key={i} className="bg-white p-2.5 rounded border border-stone-200/80">
                        <span className="block text-stone-500 text-[11px]">{spec.label}</span>
                        <span className="block font-bold text-stone-900 mt-0.5">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                  {parsedSpecs.length > 4 && (
                    <a
                      href="#specifikationer-sektion"
                      className="inline-block mt-3 text-xs font-semibold text-[#006368] hover:underline"
                    >
                      View all {parsedSpecs.length} specifications below <i className="fa-solid fa-arrow-down ml-1" />
                    </a>
                  )}
                </div>
              )}

              {/* Contact and Inquiry Actions */}
              <div className="mt-8 pt-6 border-t border-stone-200 flex flex-col sm:flex-row gap-3">
                <a
                  href="#kontakt-sektion"
                  className="px-6 py-3.5 rounded bg-[#006368] hover:bg-[#004d51] text-white font-bold text-sm text-center tracking-wide transition shadow-sm flex items-center justify-center gap-2"
                >
                  <i className="fa-solid fa-envelope" />
                  <span>Request Information</span>
                </a>
                <a
                  href="tel:57526262"
                  className="px-5 py-3.5 rounded bg-stone-100 hover:bg-stone-200 text-stone-800 font-semibold text-sm text-center border border-stone-300 transition whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="fa-solid fa-phone text-[#006368]" />
                  <span>Call +45 57 52 62 62</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Content Sections */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Main content column */}
            <div className="lg:col-span-8 space-y-12">
              {/* Detailed Description */}
              {product.lang_beskrivelse && (
                <div className="bg-white p-6 sm:p-8 rounded-xl border border-stone-200 shadow-sm">
                  <h2 className="text-xl font-bold text-stone-900 mb-4 pb-3 border-b border-stone-100 flex items-center gap-2">
                    <i className="fa-solid fa-circle-info text-[#006368]" />
                    <span>Product Description</span>
                  </h2>
                  <div
                    className="prose prose-stone max-w-none text-stone-700 leading-relaxed [&>p]:mb-4 [&>p:last-child]:mb-0 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-1.5 [&>strong]:text-stone-900"
                    dangerouslySetInnerHTML={{ __html: product.lang_beskrivelse }}
                  />
                </div>
              )}

              {/* Key Features */}
              {product.centrale_egenskaber && (
                <div className="bg-white p-6 sm:p-8 rounded-xl border border-stone-200 shadow-sm">
                  <h2 className="text-xl font-bold text-stone-900 mb-4 pb-3 border-b border-stone-100 flex items-center gap-2">
                    <i className="fa-solid fa-circle-check text-[#006368]" />
                    <span>Key Features</span>
                  </h2>
                  <div
                    className="prose prose-stone max-w-none text-stone-700 leading-relaxed [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-2 [&>li]:leading-normal"
                    dangerouslySetInnerHTML={{ __html: product.centrale_egenskaber }}
                  />
                </div>
              )}

              {/* Standard Configuration */}
              {product.standardkonfiguration && (
                <div className="bg-white p-6 sm:p-8 rounded-xl border border-stone-200 shadow-sm">
                  <h2 className="text-xl font-bold text-stone-900 mb-4 pb-3 border-b border-stone-100 flex items-center gap-2">
                    <i className="fa-solid fa-gear text-[#006368]" />
                    <span>Standard Configuration</span>
                  </h2>
                  <div
                    className="prose prose-stone max-w-none text-stone-700 leading-relaxed [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-2 [&>li]:leading-normal"
                    dangerouslySetInnerHTML={{ __html: product.standardkonfiguration }}
                  />
                </div>
              )}

              {/* Full Technical Specifications Table */}
              <div
                id="specifikationer-sektion"
                className="bg-white p-6 sm:p-8 rounded-xl border border-stone-200 shadow-sm"
              >
                <h2 className="text-xl font-bold text-stone-900 mb-4 pb-3 border-b border-stone-100 flex items-center gap-2">
                  <i className="fa-solid fa-table-list text-[#006368]" />
                  <span>Technical Specifications</span>
                </h2>

                {product.specifikationer_tabel ? (
                  <div
                    className="overflow-x-auto [&>table]:w-full [&>table]:text-sm [&>table]:text-left [&>table]:border-collapse [&_th]:bg-stone-100 [&_th]:p-3 [&_th]:font-bold [&_th]:text-stone-800 [&_th]:border-b [&_th]:border-stone-200 [&_td]:p-3 [&_td]:border-b [&_td]:border-stone-100 [&_tr:nth-child(even)]:bg-stone-50/60"
                    dangerouslySetInnerHTML={{ __html: product.specifikationer_tabel }}
                  />
                ) : parsedSpecs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left border-collapse">
                      <thead>
                        <tr className="bg-stone-100 border-b border-stone-200">
                          <th className="p-3 font-bold text-stone-800 w-1/2">Specification</th>
                          <th className="p-3 font-bold text-stone-800 w-1/2">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedSpecs.map((spec, index) => (
                          <tr
                            key={index}
                            className={`border-b border-stone-100 transition-colors ${
                              index % 2 === 0 ? 'bg-white' : 'bg-stone-50/60'
                            }`}
                          >
                            <td className="p-3 font-medium text-stone-800">{spec.label}</td>
                            <td className="p-3 text-stone-600 font-mono text-xs sm:text-sm">
                              {spec.value}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-stone-500 italic">
                    Please contact us for detailed technical specifications for this model.
                  </p>
                )}
              </div>

              {/* Inquiry Form */}
              <div id="kontakt-sektion" className="bg-white p-6 sm:p-8 rounded-xl border border-stone-200 shadow-sm">
                <div className="mb-6">
                  <h2 className="text-xl font-bold text-stone-900 flex items-center gap-2">
                    <i className="fa-solid fa-paper-plane text-[#006368]" />
                    <span>Inquire About {product.titel}</span>
                  </h2>
                  <p className="text-sm text-stone-600 mt-1">
                    Fill out the form below and one of our machinery engineers will get back to you with guidance and technical advice.
                  </p>
                </div>
                <ContactForm
                  defaultSubject={`Inquiry regarding ${product.titel} (${product.model || ''}) - Item no: ${product.varenummer || 'N/A'}`}
                />
              </div>
            </div>

            {/* Right sidebar */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-white p-6 rounded-xl border border-stone-200 shadow-sm">
                <h3 className="text-sm font-bold uppercase tracking-wider text-stone-900 mb-3">
                  Product Category
                </h3>
                <p className="text-xs text-stone-600 mb-4">
                  Browse other machinery and equipment in this category.
                </p>
                <Link
                  to={categoryLink}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded bg-stone-100 hover:bg-[#006368] hover:text-white text-stone-800 text-xs font-bold transition duration-200"
                >
                  <i className="fa-solid fa-arrow-left" />
                  <span>Go to {categoryTitle}</span>
                </Link>
              </div>

              <div className="bg-[#1a2024] text-white rounded-xl p-6 space-y-4 shadow-sm">
                <h3 className="font-bold text-base text-white">Need Engineering Advice?</h3>
                <p className="text-xs text-stone-300 leading-relaxed">
                  Our specialists have over 100 years of combined experience with press brakes and sheet metal processing.
                </p>
                <div className="pt-2 space-y-2.5">
                  <a
                    href="tel:57526262"
                    className="flex items-center gap-3 p-3 rounded bg-white/10 hover:bg-white/20 transition text-xs font-bold whitespace-nowrap"
                  >
                    <i className="fa-solid fa-phone text-[#0d8086]" />
                    <span>+45 57 52 62 62</span>
                  </a>
                  <a
                    href="mailto:info@pmborup.dk"
                    className="flex items-center gap-3 p-3 rounded bg-white/10 hover:bg-white/20 transition text-xs font-medium whitespace-nowrap"
                  >
                    <i className="fa-solid fa-envelope text-[#0d8086]" />
                    <span>info@pmborup.dk</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox for Zoom */}
      <Lightbox
        images={mainImage ? [mainImage] : []}
        initialIndex={0}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}
